import { ApiResponse } from "../../../core/_models";
import { requestLaravel } from "../../../../../utils/api";

// Items a user adds to a project's stage section (e.g. institutional sponsors), shared by every stage.
const SECTION_ITEMS_URL = "/project-section-items";

/** The list an item belongs to: one section of one project stage. */
export interface SectionItemScope {
    project_id: number;
    /** Stage key, e.g. project_stage_0. */
    stage_id: string;
    /** Section name, e.g. institutional_sponsor. */
    section: string;
}

/** A lookup option saved on an item. */
export interface SectionItemKeyValue {
    key_value: string;
    display_name: string | null;
    lookup_dev_name: string | null;
    free_text: string | null;
}

export interface ProjectSectionItem {
    id: number;
    project_id: number;
    stage_id: string;
    section: string;
    title: string;
    description: string | null;
    /** Key name => its selected lookup options. */
    keys: Record<string, SectionItemKeyValue[]>;
    created_by: number | null;
    creation_date: string | null;
    updated_by: number | null;
    updation_date: string | null;
}

type SectionItemKeyOption = string | { key_value: string; free_text?: string | null };

/** Key name => lookup data_key, {key_value, free_text}, or a list of those; null or [] clears the key. */
export type SectionItemKeysInput = Record<string, SectionItemKeyOption | SectionItemKeyOption[] | null>;

export interface SectionItemInput {
    title: string;
    description?: string | null;
    keys?: SectionItemKeysInput;
}

export function listSectionItems(scope: SectionItemScope, signal?: AbortSignal) {
    return requestLaravel<ApiResponse<ProjectSectionItem[]>>(`${SECTION_ITEMS_URL}/list`, { ...scope }, { signal });
}

export function createSectionItem(scope: SectionItemScope, item: SectionItemInput) {
    return requestLaravel<ApiResponse<ProjectSectionItem>>(`${SECTION_ITEMS_URL}/create`, { ...scope, ...item });
}

/** Fields (and key names) left out keep their saved value. */
export function updateSectionItem(scope: SectionItemScope, id: number, item: Partial<SectionItemInput>) {
    return requestLaravel<ApiResponse<ProjectSectionItem>>(`${SECTION_ITEMS_URL}/update`, { ...scope, ...item, id });
}

export function deleteSectionItem(scope: SectionItemScope, id: number) {
    return requestLaravel<ApiResponse<[]>>(`${SECTION_ITEMS_URL}/delete`, { ...scope, id });
}

/** The first option saved under a key, or '' when there is none. */
export const sectionItemKeyValue = (item: ProjectSectionItem, keyName: string): string =>
    item.keys?.[keyName]?.[0]?.key_value ?? "";

/** The free text of the first option saved under a key, or '' when there is none. */
export const sectionItemKeyText = (item: ProjectSectionItem, keyName: string): string =>
    item.keys?.[keyName]?.[0]?.free_text ?? "";
